/**
 * EP-99 — Win-pickup LIFF hooks
 *
 * Covers three concerns:
 *   1. Fare-board image (GET /api/liff/win_pickup/fare_board)
 *   2. Create request (POST /api/liff/win_pickup_requests) + bounded poll
 *   3. Cancel request (DELETE /api/liff/win_pickup_requests/:id)
 *
 * Gate strategy (single-gate, no Unleash flag):
 *   The server returns 403 "Module not enabled" when win_pickup_enabled is off.
 *   The hooks bubble that as an AxiosError; the page reads the response status
 *   to distinguish "module off" (403) from other errors.
 *
 * Poll bound (EP99_D8):
 *   The poll on GET :id uses refetchInterval as a function that stops after
 *   10 minutes from request creation time. This means the UI will visibly flip
 *   to "expired" at the cap — never polling forever.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type { WinPickupRequest, WinPickupFareBoard } from '@/types'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Poll interval while a request is pending: 5 seconds. */
const POLL_INTERVAL_MS = 5_000

/** Hard cap: 10 minutes after creation the poll stops and status shows "expired". */
const POLL_CAP_MS = 10 * 60 * 1_000

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const winPickupKeys = {
  fareBoard: () => ['liff', 'win_pickup', 'fare_board'] as const,
  request: (id: number) => ['liff', 'win_pickup_requests', id] as const,
}

// ---------------------------------------------------------------------------
// Fare-board hook
// ---------------------------------------------------------------------------

export function useWinPickupFareBoard() {
  return useQuery({
    queryKey: winPickupKeys.fareBoard(),
    queryFn: async () => {
      const response = await api.get<WinPickupFareBoard>('/api/liff/win_pickup/fare_board')
      return response.data
    },
    // Don't retry on 403 — the module is just disabled.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 403) return false
      return failureCount < 1
    },
  })
}

// ---------------------------------------------------------------------------
// Bounded poll hook
// ---------------------------------------------------------------------------

interface UseWinPickupRequestOptions {
  id: number
  /** ISO string when the request was created — used to compute the 10-min cap. */
  createdAt: string
}

/**
 * Polls GET /api/liff/win_pickup_requests/:id at 5s intervals.
 * The interval function stops polling when:
 *   - status is not "pending" (accepted / expired / cancelled), OR
 *   - more than 10 minutes have elapsed since createdAt.
 */
export function useWinPickupRequestPoll({ id, createdAt }: UseWinPickupRequestOptions) {
  return useQuery({
    queryKey: winPickupKeys.request(id),
    queryFn: async () => {
      const response = await api.get<WinPickupRequest>(`/api/liff/win_pickup_requests/${id}`)
      return response.data
    },
    refetchInterval: (query) => {
      const data = query.state.data
      // Stop when we have a terminal status
      if (data && data.status !== 'pending') return false

      // Stop when we have passed the 10-minute cap
      const elapsed = Date.now() - new Date(createdAt).getTime()
      if (elapsed >= POLL_CAP_MS) return false

      return POLL_INTERVAL_MS
    },
    // Don't use stale data to determine polling state — always check fresh
    staleTime: 0,
    // Keep polling even when the window is unfocused (LIFF users switch apps)
    refetchIntervalInBackground: true,
    retry: false,
  })
}

// ---------------------------------------------------------------------------
// Create mutation
// ---------------------------------------------------------------------------

export interface CreateWinPickupResult {
  request: WinPickupRequest
  createdAt: string
}

export function useCreateWinPickupRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<CreateWinPickupResult> => {
      const now = new Date().toISOString()
      const response = await api.post<WinPickupRequest>('/api/liff/win_pickup_requests')
      return { request: response.data, createdAt: now }
    },
    onSuccess: ({ request }) => {
      // Seed the cache so the poll starts immediately with the created status.
      queryClient.setQueryData(winPickupKeys.request(request.id), request)
    },
  })
}

// ---------------------------------------------------------------------------
// Cancel mutation
// ---------------------------------------------------------------------------

export function useCancelWinPickupRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/liff/win_pickup_requests/${id}`)
    },
    onSuccess: (_data, id) => {
      // Optimistically update the cached request to cancelled status.
      queryClient.setQueryData(winPickupKeys.request(id), (old: WinPickupRequest | undefined) => {
        if (!old) return old
        return { ...old, status: 'cancelled' as const }
      })
    },
  })
}

// ---------------------------------------------------------------------------
// State management for the active request session
// ---------------------------------------------------------------------------

export interface WinPickupSession {
  requestId: number
  createdAt: string
}

/**
 * Simple in-memory session for the active win-pickup request.
 * Survives page re-renders within a session but clears on navigation away
 * (intentional — the user must re-request on returning).
 */
export function useWinPickupSession() {
  const [session, setSession] = useState<WinPickupSession | null>(null)

  const startSession = (requestId: number, createdAt: string) => {
    setSession({ requestId, createdAt })
  }

  const clearSession = () => setSession(null)

  return { session, startSession, clearSession }
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { POLL_INTERVAL_MS, POLL_CAP_MS }
